import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import type { TrialStage, TrialIssue } from "@/lib/trial-schema";
import { monolithName } from "@/lib/trial-defense";
import { issuesForStage } from "@/lib/trial-issues";

interface Props {
  stages: TrialStage[];
  issues: TrialIssue[];
  selected: number;
  onSelect: (idx: number) => void;
  onAddFloor: () => void;
  onRemoveLastFloor: () => void;
}

/**
 * Floor rows on the left. Every validator issue must be reachable here —
 * badge shows the worst severity for that floor, so a broken floor never
 * hides behind "looks fine" when it isn't.
 */
export function FloorList({ stages, issues, selected, onSelect, onAddFloor, onRemoveLastFloor }: Props) {
  return (
    <div className="flex flex-col border border-border rounded-md overflow-hidden bg-card">
      <div className="flex-1 overflow-y-auto max-h-[70vh]">
        {stages.map((stage, i) => {
          const stageIssues = issuesForStage(issues, i);
          const errorCount = stageIssues.filter((x) => x.severity === "error").length;
          const warningCount = stageIssues.filter((x) => x.severity === "warning").length;
          const waveCount = stage.waveData.length;
          const enemyCount = stage.waveData.reduce((n, w) => n + w.spawnData.length, 0);
          return (
            <button
              key={i}
              type="button"
              onClick={() => onSelect(i)}
              className={cn(
                "w-full text-left px-3 py-2 border-b border-border/50 last:border-0 text-xs flex items-center gap-2 transition-colors",
                selected === i ? "bg-accent" : "hover:bg-accent/40"
              )}
            >
              <span className="font-mono w-9 shrink-0">{stage.floor}</span>
              <span className="flex-1 min-w-0 truncate text-muted-foreground">
                {monolithName(stage.defenseSystem)}
              </span>
              <span className="text-[10px] text-muted-foreground shrink-0 whitespace-nowrap">
                {waveCount}w · {enemyCount}e
              </span>
              {errorCount > 0 && (
                <Badge variant="destructive" className="shrink-0 px-1.5">
                  {errorCount}
                </Badge>
              )}
              {errorCount === 0 && warningCount > 0 && (
                <Badge variant="secondary" className="shrink-0 px-1.5">
                  {warningCount}
                </Badge>
              )}
            </button>
          );
        })}
      </div>
      <div className="flex items-center gap-2 p-2 border-t border-border">
        <button
          type="button"
          onClick={onAddFloor}
          className="flex-1 text-xs px-2 py-1.5 rounded-md border border-border hover:bg-accent transition-colors"
        >
          + Add floor
        </button>
        <button
          type="button"
          onClick={onRemoveLastFloor}
          disabled={stages.length === 0}
          className="text-xs px-2 py-1.5 rounded-md border border-border hover:bg-destructive/10 text-destructive transition-colors disabled:opacity-40"
        >
          Remove last
        </button>
      </div>
    </div>
  );
}
